import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { FormState, FormSubmitHandler, FormValidation, FormValidationState, MyChangeEvent, MyFocusEvent } from "./models";
import { InputType } from "./contants";

// Move useDebounce outside the component to prevent recreation on each render
function createDebouncedFunction(func: Function, delay: number) {
  let timeoutId: NodeJS.Timeout | null = null

  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

// Define the form state type
type FormReducerState<T> = {
  formState: FormState<T>
  formValidation: FormValidationState
  isSubmitted: boolean
  touchedFields: Set<string>
  dirtyFields: Set<string>
  errors: FormValidationState
}

// Define action types
type FormReducerAction<T> =
  | { type: "SET_FORM_STATE"; payload: Partial<FormState<T>> }
  | { type: "VALIDATE_FIELD"; field: string; error: string | null }
  | { type: "VALIDATE_FORM"; validation: FormValidationState }
  | { type: "BLUR_FIELD"; field: string; error: string | null }
  | { type: "SUBMIT_FORM"; validation: FormValidationState }
  | { type: "RESET"; initialForm: FormState<T> }

// Create a reducer function to handle all state updates
function formReducer<T>(state: FormReducerState<T>, action: FormReducerAction<T>): FormReducerState<T> {
  switch (action.type) {
    case "SET_FORM_STATE":
      return {
        ...state,
        formState: { ...state.formState, ...action.payload },
      }

    case "VALIDATE_FIELD": {
      // Just update validation without affecting errors
      return {
        ...state,
        formValidation: {
          ...state.formValidation,
          [action.field]: action.error,
        },
      }
    }

    case "VALIDATE_FORM":
      // Update all validation results at once
      return {
        ...state,
        formValidation: action.validation,
      }

    case "BLUR_FIELD": {
      // Handle field blur - update touched, validation, and errors in one action
      const newTouched = new Set(state.touchedFields)
      newTouched.add(action.field)

      const newValidation = {
        ...state.formValidation,
        [action.field]: action.error,
      }

      const newErrors = { ...state.errors }
      if (action.error === null) {
        delete newErrors[action.field]
      } else {
        newErrors[action.field] = action.error
      }

      return {
        ...state,
        touchedFields: newTouched,
        formValidation: newValidation,
        errors: newErrors,
      }
    }

    case "SUBMIT_FORM": {
      // Handle form submission - update validation, errors, and isSubmitted in one action
      const submissionErrors: FormValidationState = {}

      // Only include errors for invalid fields
      for (const field in action.validation) {
        if (action.validation[field] !== null) {
          submissionErrors[field] = action.validation[field]
        }
      }

      return {
        ...state,
        formValidation: action.validation,
        errors: submissionErrors,
        isSubmitted: true,
      }
    }

    case "RESET":
      return {
        formState: action.initialForm,
        formValidation: {},
        isSubmitted: false,
        touchedFields: new Set(),
        dirtyFields: new Set(),
        errors: {},
      }

    default:
      return state
  }
}

export function useForm<TFieldValues extends Record<string, any>>(
  initialForm: FormState<TFieldValues> = {} as FormState<TFieldValues>,
  formValidations: FormValidation = {},
) {
  // Use useReducer instead of multiple useState calls
  const [state, dispatch] = useReducer(formReducer<TFieldValues>, {
    formState: initialForm,
    formValidation: {},
    isSubmitted: false,
    touchedFields: new Set<string>(),
    dirtyFields: new Set<string>(),
    errors: {},
  })

  const { formState, formValidation, isSubmitted, touchedFields, dirtyFields, errors } = state

  const subscribers = useRef<Set<Function>>(new Set())
  const isMounted = useRef(true)
  const debouncedValidateRef = useRef<Function | null>(null)

  // For debugging renders
  const renderCount = useRef(0)
  useEffect(() => {
    renderCount.current += 1
    // Uncomment to debug renders
    // console.log(`Render #${renderCount.current}`);
  })

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  // Validate a single field
  const validateField = useCallback(
    async (name: string, value: any) => {
      if (!formValidations[name]) return null

      const [validatorFn, errorMessage] = formValidations[name]
      const isValid = await validatorFn(value)
      return isValid ? null : errorMessage
    },
    [formValidations],
  )

  // Validate the entire form
  const validateForm = useCallback(
    async (state: FormState<TFieldValues>) => {
      const formCheckedValues: FormValidationState = {}
      for (const key in formValidations) {
        const [validatorFn, errorMessage] = formValidations[key]
        const value = state[key as keyof TFieldValues]
        const isValid = await validatorFn(value)
        formCheckedValues[key] = isValid ? null : errorMessage
      }

      return formCheckedValues
    },
    [formValidations],
  )

  // Initialize the debounced function only once
  useEffect(() => {
    debouncedValidateRef.current = createDebouncedFunction(async (state: FormState<TFieldValues>) => {
      if (isMounted.current) {
        const validation = await validateForm(state)
        dispatch({ type: "VALIDATE_FORM", validation })
      }
    }, 500)
  }, [validateForm])

  // Update validation state when form changes, but don't show errors yet
  const isInitialMount = useRef(true)
  useEffect(() => {
    // Skip validation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (debouncedValidateRef.current) {
      debouncedValidateRef.current(formState)
    }
  }, [formState])

  const isFormValid = useMemo(() => {
    if (Object.keys(formState).length === 0) return false

    for (const formValue of Object.keys(formValidation)) {
      if (formValidation[formValue] !== null) return false
    }

    return true
  }, [formValidation, formState])

  const subscribe = useCallback((fn: Function) => {
    subscribers.current.add(fn)
    return () => subscribers.current.delete(fn)
  }, [])

  const notifySubscribers = useCallback(() => {
    subscribers.current.forEach((fn) => fn())
  }, [])

  const onInputChange = useCallback(
    (event: MyChangeEvent) => {
      const { name, value, type, checked, files } = event.target as HTMLInputElement
      const initialValue = initialForm[name as keyof TFieldValues]
      let newValue: string | boolean | File | number = value

      if (type === InputType.CHECKBOX) {
        newValue = checked
      } else if (type === InputType.FILE && files) {
        newValue = files[0]
      } else if (typeof initialValue === "number") {
        newValue = Number(value)
      }

      // Si el valor cambió, actualizamos el estado y los dirtyFields
      if (formState[name as keyof TFieldValues] !== newValue) {
        dispatch({
          type: "SET_FORM_STATE",
          payload: { [name]: newValue } as Partial<FormState<TFieldValues>>,
        })
      }
    },
    [formState, initialForm],
  )

  const onResetForm = useCallback(() => {
    dispatch({ type: "RESET", initialForm })
  }, [initialForm])

  const onBlur = useCallback(
    async (event: MyFocusEvent) => {
      const { name } = event.target

      // Immediately validate this specific field
      const value = formState[name as keyof TFieldValues]
      const fieldError = await validateField(name, value)

      // Update touched, validation, and errors in a single dispatch
      dispatch({ type: "BLUR_FIELD", field: name, error: fieldError })
    },
    [formState, validateField],
  )

  const handleSubmit = useCallback(
    (onSubmit: FormSubmitHandler<TFieldValues>) => async (event: React.FormEvent) => {
      event.preventDefault()

      // Validate all fields immediately before submission
      const validationResult = await validateForm(formState)

      // Update validation, errors, and isSubmitted in a single dispatch
      dispatch({ type: "SUBMIT_FORM", validation: validationResult })

      // Check if form is valid based on the immediate validation result
      const isValid = Object.values(validationResult).every((error) => error === null)

      if (isValid) {
        const result = onSubmit(formState)
        if (result instanceof Promise) {
          await result
        }
      }
    },
    [formState, validateForm],
  )

  return {
    formState,
    setFormState: (newState: Partial<FormState<TFieldValues>>) =>
      dispatch({ type: "SET_FORM_STATE", payload: newState }),
    onInputChange,
    onResetForm,
    formValidation,
    errors,
    isFormValid,
    handleSubmit,
    isSubmitted,
    onBlur,
    touchedFields,
    dirtyFields,
    subscribe,
  }
}